import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import axios from "axios";
import { API_BASE_URL } from "./utilities";
import "./App.scss";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { muiTheme } from "./utilities/muiTheme";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Translator, Translate } from "react-auto-translate";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "Header";
import Sidebar from "Sidebar";
import Container from "Container";
import PageDenied from "./components/PageDenied";
import PageLoad from "./containers/PageLoad";
import BottomTabBar from "./components/BottomTabBar";

import WithLoader from "./hoc/WithLoader";
import Loader from "./components/Loader";

// ─── Route-level lazy imports (each loads only when its route is first visited) ─
const Appointments      = lazy(() => import("./containers/Appointments"));
const Campaign          = lazy(() => import("./containers/Campaign"));
const Staff             = lazy(() => import("./containers/Staff"));
const Clients           = lazy(() => import("./containers/Clients"));
const Inventory         = lazy(() => import("./containers/Inventory"));
const Expense           = lazy(() => import("./containers/Expense"));
const Invoices          = lazy(() => import("./containers/Invoices"));
const Analytics         = lazy(() => import("./containers/Analytics"));
const Settings          = lazy(() => import("./containers/Settings"));
const Payments          = lazy(() => import("./containers/Payments"));
const Memberships       = lazy(() => import("./containers/Memberships"));
const Packages          = lazy(() => import("./containers/Packages"));
const Login             = lazy(() => import("./containers/Login"));
const MarketPlace       = lazy(() => import("./containers/MarketPlace"));
const MarketplaceOrders = lazy(() => import("./containers/MarketplaceOrders"));
const StaffSalary       = lazy(() => import("./containers/StaffSalary"));
const StaffListMVP      = lazy(() => import("./containers/StaffListMVP"));
const ClientListMVP     = lazy(() => import("./containers/ClientListMVP"));
const ReleaseNotesModal  = lazy(() => import("./components/ReleaseNotes"));
const Help               = lazy(() => import("./containers/Help"));
const OnlineEnquiries    = lazy(() => import("./containers/OnlineEnquiries"));
const AdsManagement      = lazy(() => import("./containers/AdsManagement"));
const GoogleBusiness     = lazy(() => import("./containers/GoogleBusiness"));
import { branchFetchData } from "store/actions/branch";
import { clientFetchData } from "store/actions/client";
import { shiftsFetchData } from "store/actions/shifts";
import { positionsFetchData } from "store/actions/positions";
import { servicesFetchData } from "store/actions/services";
import { productsFetchData } from "store/actions/products";
import { partnerType } from "store/actions/partnerType";
import { getUserPermissions } from "./store/actions/aclUserPermissions";
import { getBranchSubscriptionsServices } from "store/actions/branchSubscriptionsServices";
import { getBranchLoyaltyPoint } from "./store/actions/branch";
import { getReleaseNotes } from "./store/actions/releaseNotes"; // eslint-disable-line no-unused-vars
import { CLOUD_TRANSALATE_API_KEY } from "./utilities/constants";

import { trackPageView } from "./utils/facebookPixel";
import FacebookRouteTracker from "./utils/FacebookRouteTracker.jsx";
import GARouteTracker from "./utils/GARouteTracker.jsx";
import ChatPane from "./components/Chatbot/ChatPane";



function ChatPaneWrapper() {
  const chatPaneOpen = useSelector((state) => state.chatbot.chatPaneOpen);
  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.style.transition = "margin-right 0.25s ease";
      mainContent.style.marginRight = chatPaneOpen ? "420px" : "0px";
    }
  }, [chatPaneOpen]);
  return <ChatPane />;
}

function App() {
  // const location = useLocation();
  const dispatch = useDispatch();
  const isLogin = useSelector((state) => state.authentication);
  const { data: language } = useSelector((state) => state.language);
  const branchData = useSelector((state) => state.branch.data);
  const branchSubscriptionsServices = useSelector((state) => state.branchSubscriptionsServices);
  const { data: userAccessLevel, isFetching: userAccessLevelFetching } = useSelector(
    (state) => state.aclUserPermissions
  );
  const user = useSelector((state) => state.user);
  const [login, setLogin] = useState(localStorage.getItem("token") || false);
  const [refreshCount, setRefresh] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [routesPathMap, setRoutesPathMap] = useState({});
  const [marketplacePageAccess, setMarketplacePageAccess] = useState(false);
  const [adsManagementAccess, setAdsManagementAccess] = useState(false);
  const [packagePageAccess, setPackagePageAccess] = useState(false);
  const [googleBusinessPageAccess, setGoogleBusinessPageAccess] = useState(false);
  const [onlineBookingPageAccess, setOnlineBookingPageAccess] = useState(false);
  const [metaLeadsAccess, setMetaLeadsAccess] = useState(false);

  // useEffect(() => {
  //   trackPageView(); 
  // }, [location.pathname]);

  const userLanguage = useMemo(() => {
    try {
      const systemLanguage = localStorage.getItem("language", language);
      if (language) {
        localStorage.setItem("language", language);
        return language;
      } else {
        return systemLanguage || "en";
      }
    } catch (error) {
      console.log(error);
    }
  }, [language]);

  useEffect(() => {
    isLogin.length && setLogin(true);
  }, [isLogin]);

  useEffect(() => {
    dispatch(partnerType());
    if (login) {
      dispatch(getUserPermissions());
    }
  }, [dispatch, login]);

  useEffect(() => {
    if (Object.keys(branchData).length === 0) {
      return;
    }
    if ("enable_marketplace" in branchData) {
      setMarketplacePageAccess(branchData.enable_marketplace);
    }
    if ("enable_package" in branchData) {
      setPackagePageAccess(branchData.enable_package);
    }
    if ("enable_google_business" in branchData) {
      setGoogleBusinessPageAccess(branchData.enable_google_business);
    }
    if ("enable_online_booking" in branchData) {
      setOnlineBookingPageAccess(branchData.enable_online_booking);
    }
  }, [branchData]);

  useEffect(() => {
    if (!userAccessLevel || userAccessLevel.formatted_component_permissions.length === 0) {
      return;
    }

    const isAdmin = userAccessLevel.formatted_component_permissions.includes("admin_access");
    dispatch(branchFetchData());
    dispatch(getBranchLoyaltyPoint());
    dispatch(clientFetchData());
    dispatch(getBranchSubscriptionsServices());

    // enable_meta_ads/enable_meta_leads are deliberately never exposed on
    // BranchSerializer/branchData (see settings.views.MetaLeadsAccessView) --
    // this is the only way the sidebar learns whether Ads Management or Meta
    // Leads (via the Online Enquiries page) are actually still turned on for
    // this branch. 403s for non-admin/owner staff (the endpoint's own gate)
    // degrade safely to "no access", same as Settings' own identical call.
    axios
      .get(`${API_BASE_URL}branch/meta/access/`, {
        headers: { Authorization: localStorage.getItem("token") },
      })
      .then((res) => {
        setAdsManagementAccess(Boolean(res.data?.ads_allowed));
        setMetaLeadsAccess(Boolean(res.data?.leads_allowed));
      })
      .catch(() => {
        setAdsManagementAccess(false);
        setMetaLeadsAccess(false);
      });
  }, [userAccessLevel]);
  // if(window.location.search === "?payments") {
  //   window.location = "https://ayesalon.com/payments/";
  // }

  useEffect(() => {
    localStorage.removeItem("theme");
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  const decideRoutesDependenciesMemo = useMemo(() => {
    if (
      !"id" in user ||
      !"isFetched" in branchSubscriptionsServices ||
      !branchSubscriptionsServices.isFetched ||
      userAccessLevelFetching ||
      !"base_permissions" in userAccessLevel ||
      userAccessLevel.base_permissions.length === 0
    ) {
      return false;
    }

    return true;
  }, [
    user,
    userAccessLevel,
    userAccessLevelFetching,
    branchSubscriptionsServices.data,
    marketplacePageAccess,
    adsManagementAccess,
    packagePageAccess,
    googleBusinessPageAccess,
    onlineBookingPageAccess,
    metaLeadsAccess,
  ]);

  useEffect(() => {
    if (!decideRoutesDependenciesMemo) {
      return;
    }

    let canAddExpenses = false;
    const subsData = branchSubscriptionsServices.data || [];
    if (subsData.length) {
      canAddExpenses = subsData.some(({ subscription_service }) => {
        return subscription_service.name === "EXPENSE_MANAGEMENT" && subscription_service.status;
      });
    }
    // Only gate on APPOINTMENT_LOADER — the calendar can render once appointments
    // are available. Staff and services load in the background and populate the
    // form dropdowns when the user actually clicks "+ Appointment".
    const AppointmentsWithLoader = WithLoader(Appointments, Loader, ["APPOINTMENT_LOADER"]);
    const ExpenseWithLoader = WithLoader(Expense, Loader, ["EXPENSE_LOADER"]);
    const StaffWithLoader = WithLoader(Staff, Loader, [
      "STAFF_LOADER",
      "POSITIONS_LOADER",
      "SHIFTS_LOADER",
      "STAFF_ABSENT_LOADER",
      "STAFF_BRACKET_LOADER",
      "STAFF_OVERTIME_LOADER",
    ]);
    const CampaignWithLoader = WithLoader(Campaign, Loader, ["SERVICES_LOADER", "PRODUCTS_LOADER"]);
    const ClientsWithLoader = WithLoader(Clients, Loader, [
      "CLIENTS_LOADER",
      "APPOINTMENT_LOADER",
      "CLIENT_MEMBERSHIP_LOADER",
    ]);

    const InventoryWithLoader = WithLoader(Inventory, Loader, ["SERVICES_LOADER", "PRODUCTS_LOADER"]);
    const MarketPlaceWithLoader = WithLoader(MarketPlace, Loader, ["PRODUCTS_LOADER"]);
    const MarketplaceOrdersWithLoader = WithLoader(MarketplaceOrders, Loader, ["ORDERS_LOADER"]);
    const InvoicesWithLoader = WithLoader(Invoices, Loader, ["APPOINTMENT_LOADER"]);

    const routesMap = {
      appointments_page: {
        path: "/",
        component: AppointmentsWithLoader,
      },
      staff_page: {
        path: "/staff",
        component: StaffWithLoader,
      },
      expenses_page: {
        path: "/expense",
        component: ExpenseWithLoader,
      },
      client_page: {
        path: "/clients",
        component: ClientsWithLoader,
      },
      inventory_page: {
        path: "/inventory",
        component: InventoryWithLoader,
      },
      invoices_page: {
        path: "/invoices",
        component: InvoicesWithLoader,
      },
      campaign_page: {
        path: "/discount",
        component: CampaignWithLoader,
      },
      subscriptions_page: {
        path: "/payments",
        component: Payments,
      },
      salary_page: {
        path: "/salary",
        component: StaffSalary,
      },
      memberships_page: {
        path: "/memberships",
        component: Memberships,
      },
      packages_page: {
        path: "/packages",
        component: Packages,
      },
      marketplace_page: {
        path: "/marketplace",
        component: MarketPlaceWithLoader,
      },
      orders_page: {
        path: "/orders",
        component: MarketplaceOrdersWithLoader,
      },
      analytics_page: {
        path: "/analytics",
        component: Analytics,
      },
      settings_page: {
        path: "/settings",
        component: Settings,
      },
      enquiries_page: {
        path: "/enquiries",
        component: OnlineEnquiries,
      },
      ads_management_page: {
        path: "/ads-management",
        component: AdsManagement,
      },
      google_business_page: {
        path: "/google-business",
        component: GoogleBusiness,
      },
    };

    let _routes = [];
    const _routesPathMap = {};

    userAccessLevel.base_permissions.forEach((permission) => {
      const { permission_name, id } = permission;
      if (
        !(permission_name in routesMap) ||
        (permission_name === "expenses_page" && !canAddExpenses) ||
        (permission_name === "marketplace_page" && !marketplacePageAccess) ||
        (permission_name === "orders_page" && !marketplacePageAccess) ||
        (permission_name === "ads_management_page" && !adsManagementAccess) ||
        (permission_name === "packages_page" && !packagePageAccess) ||
        (permission_name === "google_business_page" && !googleBusinessPageAccess) ||
        // Online Enquiries shows for either Meta Leads OR Online Booking --
        // the meta_leads half is granted purely on the branch flag (no
        // permission needed, see the unconditional push below), so here we
        // only need to additionally require onlineBookingPageAccess when
        // metaLeadsAccess itself is off.
        (permission_name === "enquiries_page" && !onlineBookingPageAccess && !metaLeadsAccess)
      ) {
        return;
      }

      const route = <Route exact key={id} {...routesMap[permission_name]} />;
      _routes.push(route);
      _routesPathMap[routesMap[permission_name].path] = true;
    });

    // Meta Leads unconditionally unlocks Online Enquiries regardless of
    // whether the user holds the enquiries_page permission at all -- the
    // forEach above only ever visits permissions the user already has, so a
    // user without enquiries_page but with Meta Leads enabled on the branch
    // needs this separate injection to still see the page/sidebar icon.
    if (metaLeadsAccess && !_routesPathMap[routesMap.enquiries_page.path]) {
      _routes.push(<Route exact key="enquiries_page-meta-leads" {...routesMap.enquiries_page} />);
      _routesPathMap[routesMap.enquiries_page.path] = true;
    }

    _routes.push(
      <Route key={-5} exact path="/help" component={Help} />,
      <Route key={-4} exact path="/staff-list-mvp" component={StaffListMVP} />,
      <Route key={-3} exact path="/client-list-mvp" component={ClientListMVP} />,
      <Route key={-2} exact path="/pageload" component={PageLoad} />,
      <Route key={-1} path="*" component={PageDenied} />
    );
    setRoutes(_routes);
    setRoutesPathMap(_routesPathMap);
    // adsManagementAccess must be listed here too, not just folded into
    // decideRoutesDependenciesMemo above -- that memo's own true/false gate
    // never actually checks it, so once it settles to true it stays true
    // regardless of what adsManagementAccess does afterward. Depending on
    // only the memo meant this effect fired once, capturing whichever value
    // adsManagementAccess happened to hold at that instant (usually still
    // its default false, since /branch/meta/access/ is a separate async
    // call that often resolves after the gate opens) and then never re-ran
    // when the real value arrived a moment later -- exactly why Ads
    // Management could go missing from the sidebar on a fresh load/refresh.
  }, [decideRoutesDependenciesMemo, adsManagementAccess, metaLeadsAccess, onlineBookingPageAccess]);

  const cacheProvider = {
    get: (language, key) => ((JSON.parse(localStorage.getItem("translations")) || {})[key] || {})[language],
    set: (language, key, value) => {
      const existing = JSON.parse(localStorage.getItem("translations")) || {
        [key]: {},
      };
      existing[key] = { ...existing[key], [language]: value };
      localStorage.setItem("translations", JSON.stringify(existing));
    },
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
    <div className="App">
      <Translator
        cacheProvider={cacheProvider}
        from="en"
        to={userLanguage || "en"}
        googleApiKey={CLOUD_TRANSALATE_API_KEY}
      >
        {/* {!login ? (
          <Router>
            <Route
              exact
              path="/:reset/:username?/:token?"
              render={() => <Login onClick={(state) => setLogin(state)} />}
            />
            <Route exact path="/" render={() => <Login onClick={(state) => setLogin(state)} />} />
          </Router>
        ) : (
          <Router>
            <Sidebar key={0} user={user.username} pathMap={routesPathMap} />
            <div key={1} className="main-content">
              <Header refresh={() => setRefresh(refreshCount + 1)} pathMap={routesPathMap} />
              <Container>
                <Switch>{routes}</Switch>
              </Container>
            </div>
            <ReleaseNotesModal />
          </Router>
        )} */}
        {!login ? (
          <Router basename="/app">
            <FacebookRouteTracker />
            <GARouteTracker />
            <Suspense fallback={<Loader loadingText="Loading..." />}>
              <Switch>
                <Route
                  exact
                  path="/reset/:username?/:token?"
                  render={() => <Login onClick={(state) => setLogin(state)} />}
                />
                <Route exact path="/" render={() => <Login onClick={(state) => setLogin(state)} />} />
              </Switch>
            </Suspense>
          </Router>
        ) : (
          <Router basename="/app">
            <FacebookRouteTracker />
            <GARouteTracker />
            <Sidebar key={0} user={user.username} pathMap={routesPathMap} />
            <ChatPaneWrapper />
            <div key={1} className="main-content">
              <Header refresh={() => setRefresh(refreshCount + 1)} pathMap={routesPathMap} />
              <Container>
                <Suspense fallback={<Loader loadingText="Loading..." />}>
                  <Switch>{routes}</Switch>
                </Suspense>
              </Container>
            </div>
            <BottomTabBar pathMap={routesPathMap} />
            <Suspense fallback={null}>
              <ReleaseNotesModal />
            </Suspense>
          </Router>
        )}

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Translator>
    </div>
    </ThemeProvider>
  );
}

export default App;

